import { performanceNowMs } from "./map-performance-collector.js";
import {
  assertWasmThreadingReady,
  collectMapRuntimeResourceDiscovery,
  detectWasmThreadingReadiness,
  mapRuntimePrewarmEnabled,
  resolveMapRuntimeMemoryBudgetMiB,
  resolveWasmThreadWorkerCount,
} from "./map-runtime-settings.js";
import { resolveMapRuntimeManifestUrl } from "./map-runtime-urls.js";

function classifyMapRuntimeRequest(input) {
  const rawUrl = typeof input === "string" ? input : String(input?.url || input || "");
  try {
    const url = new URL(rawUrl, globalThis.location?.href || "https://fishystuff.fish");
    if (/\/map\/runtime-manifest(?:\.|$)/.test(url.pathname)) {
      return "runtime_manifest";
    }
    if (/\/map\/runtime\/[^/]+\/[^/]+\.wasm$/.test(url.pathname)) {
      return "runtime_wasm";
    }
    if (/\/map\/runtime\/[^/]+\//.test(url.pathname)) {
      return "runtime_asset";
    }
    return "other";
  } catch (_) {
    return "other";
  }
}

function recordPerfEvent(callback, event) {
  if (typeof callback === "function") {
    callback({
      atMs: performanceNowMs(),
      ...(event || {}),
    });
  }
}

async function fetchWithPerf(fetchImpl, input, init, onPerfEvent) {
  const startedAtMs = performanceNowMs();
  const classification = classifyMapRuntimeRequest(input);
  try {
    const response = await fetchImpl(input, init);
    recordPerfEvent(onPerfEvent, {
      type: "network",
      classification,
      url: typeof input === "string" ? input : String(input?.url || input || ""),
      method: String(init?.method || input?.method || "GET"),
      cacheMode: String(init?.cache || input?.cache || ""),
      status: Number(response?.status || 0),
      ok: response?.ok === true,
      durationMs: performanceNowMs() - startedAtMs,
    });
    return response;
  } catch (error) {
    recordPerfEvent(onPerfEvent, {
      type: "network",
      classification,
      url: typeof input === "string" ? input : String(input?.url || input || ""),
      method: String(init?.method || input?.method || "GET"),
      cacheMode: String(init?.cache || input?.cache || ""),
      ok: false,
      durationMs: performanceNowMs() - startedAtMs,
      error: error && typeof error === "object" && "message" in error
        ? String(error.message)
        : String(error),
    });
    throw error;
  }
}

function mapRuntimeManifestLoadError(manifestUrl, status) {
  return new Error(`failed to load map runtime manifest: ${manifestUrl} (${status || "unknown"})`);
}

function mapRuntimeAssetLoadError(assetUrl, status) {
  return new Error(`failed to load map runtime asset: ${assetUrl} (${status || "unknown"})`);
}

function normalizeManifestAssetList(manifest, manifestUrl) {
  if (!Array.isArray(manifest?.assets)) {
    throw new Error(`invalid map runtime manifest: missing assets in ${manifestUrl}`);
  }
  return manifest.assets.map((asset) => {
    const path = String(asset || "").trim();
    if (!path) {
      throw new Error(`invalid map runtime manifest: empty asset in ${manifestUrl}`);
    }
    return new URL(path, manifestUrl).toString();
  });
}

function normalizeByteCount(value) {
  const number = Number.parseInt(value, 10);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

export function notifyMapRuntimeLoadProgress(callback, detail) {
  if (typeof callback !== "function") {
    return;
  }
  callback({
    stage: String(detail?.stage || "loading"),
    progress: Number.isFinite(detail?.progress) ? Math.min(1, Math.max(0, detail.progress)) : null,
    loadedBytes: normalizeByteCount(detail?.loadedBytes),
    totalBytes: normalizeByteCount(detail?.totalBytes),
    url: String(detail?.url || ""),
  });
}

async function fetchMapRuntimeWasm({
  wasmUrl,
  fetchImpl = globalThis.fetch?.bind(globalThis),
  onLoadProgress,
  onPerfEvent,
} = {}) {
  const url = String(wasmUrl || "").trim();
  if (!url) {
    throw new Error("FishyMapBridge requires a wasm URL in the runtime manifest");
  }
  if (typeof fetchImpl !== "function") {
    throw new Error("FishyMapBridge requires fetch() to load the runtime wasm");
  }
  notifyMapRuntimeLoadProgress(onLoadProgress, {
    stage: "wasm-fetch",
    progress: 0.1,
    loadedBytes: 0,
    totalBytes: null,
    url,
  });
  const response = await fetchWithPerf(fetchImpl, url, { cache: "default" }, onPerfEvent);
  if (!response?.ok) {
    throw mapRuntimeAssetLoadError(url, response?.status);
  }

  const totalBytes = normalizeByteCount(response.headers?.get?.("Content-Length"));
  notifyMapRuntimeLoadProgress(onLoadProgress, {
    stage: "wasm-fetch",
    progress: null,
    loadedBytes: null,
    totalBytes,
    url,
  });
  return response;
}

export async function loadMapRuntimeManifest({
  locationLike = globalThis.location,
  cdnBaseUrl = globalThis.window?.__fishystuffCdnBaseUrl,
  fetchImpl = globalThis.fetch?.bind(globalThis),
  onPerfEvent,
} = {}) {
  if (typeof fetchImpl !== "function") {
    throw new Error("FishyMapBridge requires fetch() to load the runtime manifest");
  }
  const manifestUrl = resolveMapRuntimeManifestUrl({
    locationLike,
    explicitBaseUrl: cdnBaseUrl,
  });
  const response = await fetchWithPerf(fetchImpl, manifestUrl, {
    cache: "no-store",
  }, onPerfEvent);
  if (!response?.ok) {
    throw mapRuntimeManifestLoadError(manifestUrl, response?.status);
  }
  const manifest = await response.json();
  const schemaVersion = Number(manifest?.schemaVersion ?? manifest?.schema_version ?? 0);
  if (schemaVersion !== 2) {
    throw new Error(`invalid map runtime manifest: unsupported schema in ${manifestUrl}`);
  }
  const modulePath = String(manifest?.module || "").trim();
  if (!modulePath) {
    throw new Error(`invalid map runtime manifest: missing module in ${manifestUrl}`);
  }
  const wasmPath = String(manifest?.wasm || "").trim();
  if (!wasmPath) {
    throw new Error(`invalid map runtime manifest: missing wasm in ${manifestUrl}`);
  }
  const assets = normalizeManifestAssetList(manifest, manifestUrl);
  const moduleUrl = new URL(modulePath, manifestUrl).toString();
  const wasmUrl = new URL(wasmPath, manifestUrl).toString();
  if (!assets.includes(moduleUrl) || !assets.includes(wasmUrl)) {
    throw new Error(`invalid map runtime manifest: assets must include module and wasm in ${manifestUrl}`);
  }
  const threaded = manifest.threaded === true;
  const threadPoolExport = String(manifest?.threadPoolExport || "").trim();
  if (threaded && !threadPoolExport) {
    throw new Error(`invalid map runtime manifest: missing threadPoolExport in ${manifestUrl}`);
  }
  recordPerfEvent(onPerfEvent, {
    type: "runtimeManifest",
    manifestUrl,
    moduleUrl,
    wasmUrl,
    schemaVersion,
    threaded,
    assetCount: assets.length,
    threadPoolExport: threaded ? threadPoolExport : "",
  });
  return {
    manifestUrl,
    moduleUrl,
    wasmUrl,
    assets,
    threaded,
    threadPoolExport: threaded ? threadPoolExport : "",
    manifest,
  };
}

function mapRuntimeCacheKeyFromManifest(manifestInfo) {
  const modulePath = new URL(manifestInfo.moduleUrl).pathname;
  const match = modulePath.match(/\/map\/runtime\/([^/]+)\//);
  return match ? match[1] : "";
}

async function registerMapRuntimeServiceWorker({
  navigatorLike = globalThis.navigator,
  serviceWorkerUrl = "/map/map-runtime-sw.js",
  scope = "/map/",
} = {}) {
  if (!navigatorLike?.serviceWorker?.register) {
    return null;
  }
  try {
    return await navigatorLike.serviceWorker.register(serviceWorkerUrl, { scope });
  } catch (_) {
    return null;
  }
}

async function prewarmMapRuntimeCache(manifestInfo, options = {}) {
  if (!manifestInfo?.assets?.length) {
    recordPerfEvent(options.onPerfEvent, {
      type: "cache",
      operation: "prewarm-skip",
      reason: "no-assets",
    });
    return false;
  }
  const registration = await registerMapRuntimeServiceWorker(options);
  const worker =
    registration?.active ||
    registration?.waiting ||
    registration?.installing ||
    globalThis.navigator?.serviceWorker?.controller ||
    null;
  if (!worker?.postMessage) {
    recordPerfEvent(options.onPerfEvent, {
      type: "cache",
      operation: "prewarm-skip",
      reason: "no-worker",
      assetCount: manifestInfo.assets.length,
    });
    return false;
  }
  worker.postMessage({
    type: "fishymap-runtime-prewarm",
    manifestUrl: manifestInfo.manifestUrl,
    cacheKey: mapRuntimeCacheKeyFromManifest(manifestInfo),
    assets: manifestInfo.assets,
  });
  recordPerfEvent(options.onPerfEvent, {
    type: "cache",
    operation: "prewarm-postmessage",
    assetCount: manifestInfo.assets.length,
    cacheKey: mapRuntimeCacheKeyFromManifest(manifestInfo),
  });
  return true;
}

export async function loadMapRuntimeModule(options = {}) {
  const onLoadProgress = options.onLoadProgress;
  const onPerfEvent = options.onPerfEvent;
  const globalLike = options.globalLike ?? globalThis;
  const locationLike = options.locationLike ?? globalThis.location;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch?.bind(globalThis);
  notifyMapRuntimeLoadProgress(onLoadProgress, {
    stage: "manifest",
    progress: 0.02,
  });
  const manifestInfo = await loadMapRuntimeManifest({
    locationLike,
    cdnBaseUrl: options.cdnBaseUrl,
    fetchImpl,
    onPerfEvent,
  });
  const { moduleUrl, wasmUrl } = manifestInfo;
  const threadingReadiness = detectWasmThreadingReadiness(globalLike);
  let workerCount = 0;
  if (manifestInfo.threaded) {
    assertWasmThreadingReady(threadingReadiness);
    workerCount = resolveWasmThreadWorkerCount({
      locationLike,
      explicitValue: options.wasmThreadCount,
      hardwareConcurrency: threadingReadiness.hardwareConcurrency,
      deviceMemory: threadingReadiness.deviceMemory,
    });
  }
  recordPerfEvent(onPerfEvent, {
    type: "threadStatus",
    manifestThreaded: manifestInfo.threaded === true,
    workerCount,
    threadPoolStarted: false,
    readiness: threadingReadiness,
  });
  const memoryBudgetMiB = resolveMapRuntimeMemoryBudgetMiB({
    locationLike,
    explicitValue: options.memoryBudgetMiB,
    deviceMemory: threadingReadiness.deviceMemory,
  });
  const resourceDiscovery = collectMapRuntimeResourceDiscovery({
    globalLike,
    locationLike,
    manifestInfo,
    wasmThreadCount: workerCount,
    memoryBudgetMiB,
  });
  if (
    mapRuntimePrewarmEnabled({
      locationLike,
      explicitValue: options.prewarmRuntimeCache ?? options.runtimeCachePrewarm,
    })
  ) {
    prewarmMapRuntimeCache(manifestInfo, options).catch(() => {});
  } else {
    recordPerfEvent(onPerfEvent, {
      type: "cache",
      operation: "prewarm-skip",
      reason: "disabled",
      assetCount: manifestInfo.assets?.length || 0,
      cacheKey: mapRuntimeCacheKeyFromManifest(manifestInfo),
    });
  }
  notifyMapRuntimeLoadProgress(onLoadProgress, {
    stage: "module-import",
    progress: 0.08,
    url: moduleUrl,
  });
  const importModuleImpl = options.importModuleImpl || ((url) => import(url));
  const importStartedAtMs = performanceNowMs();
  const modulePromise = importModuleImpl(moduleUrl);
  const wasmInitInput = fetchMapRuntimeWasm({
    wasmUrl,
    fetchImpl,
    onLoadProgress,
    onPerfEvent,
  });
  let wasmModule;
  try {
    wasmModule = await modulePromise;
    recordPerfEvent(onPerfEvent, {
      type: "network",
      classification: "runtime_module",
      url: moduleUrl,
      method: "IMPORT",
      cacheMode: "",
      status: 0,
      ok: true,
      durationMs: performanceNowMs() - importStartedAtMs,
    });
  } catch (error) {
    wasmInitInput.catch(() => {});
    recordPerfEvent(onPerfEvent, {
      type: "network",
      classification: "runtime_module",
      url: moduleUrl,
      method: "IMPORT",
      cacheMode: "",
      status: 0,
      ok: false,
      durationMs: performanceNowMs() - importStartedAtMs,
      error: error && typeof error === "object" && "message" in error
        ? String(error.message)
        : String(error),
    });
    throw error;
  }
  notifyMapRuntimeLoadProgress(onLoadProgress, {
    stage: "module-import",
    progress: 0.1,
    url: moduleUrl,
  });
  return {
    wasmModule,
    wasmInitInput,
    moduleUrl,
    wasmUrl,
    manifestUrl: manifestInfo.manifestUrl,
    assets: manifestInfo.assets,
    threaded: manifestInfo.threaded,
    threadPoolExport: manifestInfo.threadPoolExport,
    threadingReadiness,
    workerCount,
    memoryBudgetMiB,
    resourceDiscovery,
    manifestInfo,
    globalLike,
    locationLike,
  };
}
